/**
 * 정산 시작일 — 화면 문구와 판단 (순수 함수).
 *
 * 계획: ~/md/plan/콜봇-월정산-정산시작일-엑셀내보내기-계획.md 3-3절(파일 표)·4-3절(상세 표시·정산 대상 아님)·4-4절(시작일 수정
 * 모달 — 문구는 그대로 옮겼다). 서버 계약: ~/md/back/콜봇-월정산-정산시작일-엑셀내보내기-call-api요청.md "요청 계약".
 * 에이전트 간 약속(칸 이름·없을 때 해석): 2·3단계 계약서 1·3-1절.
 *
 *  - 시작일은 KST 날짜('YYYY-MM-DD'). 범위 = 2026-09-01 ~ (지금 KST 달 + 12개월) 말일. 지금 시각은 인자로 받는다.
 *  - 범위(scope): 'in'(시작일 미설정 또는 시작 월 뒤) · 'start_month' · 'before_start'(정산 대상 아님). 모르는 값은 'in'.
 *    상세 `payment_scope`(수납 축)와 섞지 않는다.
 *  - 새 칸이 없는 응답(call-api 배포 전)은 지금 화면과 같다 — 칩은 「시작일 미설정」.
 *  - 오류 문구는 `error.code` 로 나누고, 해결 방법을 말한다.
 *
 * vue·antd·xlsx·dayjs 를 import 하지 않는다 — node --test 로 검증한다(service/__tests__/settlementStart.test.mjs).
 */
import { EMPTY, addMonths, currentMonthKst, daysInMonth, formatKstDateTime, formatNumber, formatWon, isNumber, kstParts } from './settlementFormat.js';
import { notBillableTooltip, isNotBillableDetail, unitsMinutesText } from './settlementRules.js';
import {
  getCallApiErrorBody,
  getCallApiErrorCode,
  getCallApiErrorStatus,
  isCallApiUnimplemented,
} from '../../service/callApiError.js';
import { supportEndedText } from '../../../../utils/standardErrors.js';

// ---------------------------------------------------------------------------
// 상수
// ---------------------------------------------------------------------------

/** 고를 수 있는 가장 이른 시작일 — 정산 따라잡기 시작 달(BILLING_CATCH_UP_FROM)의 1일 */
export const START_DATE_MIN = '2026-09-01';
/** 메모 최대 글자 수(trim 뒤, 서버와 같음) */
export const START_MEMO_MAX = 500;
/** 미리 볼 수 있는 가장 먼 달 — 지금 달 + 12개월 */
export const START_DATE_MAX_MONTHS_AHEAD = 12;

/** 바꾸는 이유(a-select options) — 서버 reason_code 넷 */
export const START_REASONS = Object.freeze([
  Object.freeze({ value: 'CONTRACT_START', label: '계약 시작' }),
  Object.freeze({ value: 'TEST_PERIOD', label: '테스트 기간 끝' }),
  Object.freeze({ value: 'CORRECTION', label: '잘못 넣은 날짜 고침' }),
  Object.freeze({ value: 'OTHER', label: '기타' }),
]);

export const START_UNIMPLEMENTED_TEXT = '이 환경의 call-api 에 정산 시작일 API 가 아직 없습니다(배포 전).';
export const NOT_STARTED_RELOAD_TEXT = '정산 시작일 전 달이라 정산하지 않습니다. 화면을 새로 고칩니다.';
export const NEEDS_RECALC_TEXT = '다시 계산 필요';
export const NEEDS_RECALC_TOOLTIP = '정산서를 만든 뒤 정산 시작일이 바뀌었습니다. 다시 계산해야 확정할 수 있습니다.';
/** 범위 이름(엑셀 '정산 범위' 열도 이 값) */
export const SCOPE_LABELS = Object.freeze({
  unset: '시작일 미설정',
  in: '정산 대상',
  start_month: '시작 월',
  before_start: '정산 대상 아님',
});
/** 저장 실패의 그 밖 문구 */
export const START_SAVE_FAILED_TEXT = '정산 시작일을 저장하지 못했습니다. 잠시 후 다시 시도하세요.';
/** 미리보기(GET) 실패의 그 밖 문구 — 아직 저장하려 한 것이 아니다. 오류 줄 옆 [다시 불러오기]로 같은 입력을 다시 받는다 */
export const START_PREVIEW_FAILED_TEXT = '바뀌는 달을 불러오지 못했습니다. [다시 불러오기]를 누르세요.';

const SCOPES = new Set(['in', 'start_month', 'before_start']);
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const arr = (v) => (Array.isArray(v) ? v : []);
const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : null);
const num = (v) => (isNumber(v) ? v : null);
const pad2 = (n) => String(n).padStart(2, '0');
const hangul = (s) => typeof s === 'string' && /[가-힣]/.test(s);

// ---------------------------------------------------------------------------
// 날짜
// ---------------------------------------------------------------------------

/**
 * 'YYYY-MM-DD' 가 실제 달력 날짜인가
 * @param {unknown} v
 * @returns {boolean}
 */
export function isDateKey(v) {
  const m = typeof v === 'string' ? DATE_RE.exec(v) : null;
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (mo < 1 || mo > 12 || d < 1) return false;
  return d <= daysInMonth(`${m[1]}-${m[2]}`) && y > 0;
}

const dateOrNull = (v) => (isDateKey(v) ? v : null);
const isMonthKey = (v) => typeof v === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(v);
const dayOf = (dateKey) => Number(dateKey.slice(8, 10));

/**
 * 'YYYY-MM-DD' → 'M/D'
 * @param {unknown} dateKey
 * @returns {string} 예: '2026-10-10' → '10/10', '2026-10-01' → '10/1'. 날짜가 아니면 ''
 */
export function shortDateText(dateKey) {
  if (!isDateKey(dateKey)) return '';
  return `${Number(dateKey.slice(5, 7))}/${dayOf(dateKey)}`;
}

/** 'YYYY-MM-DD' 에 n일 더하기(UTC 달력 — 날짜만 다룬다) */
function addDays(dateKey, n) {
  const [y, m, d] = dateKey.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
}

/**
 * DatePicker 로 고를 수 있는 범위
 * @param {number} [nowMs]
 * @returns {{ min: string, max: string }} max = (nowMs 의 KST 달 + 12개월) 말일
 */
export function startDateRange(nowMs = Date.now()) {
  const lastMonth = addMonths(currentMonthKst(nowMs), START_DATE_MAX_MONTHS_AHEAD);
  return { min: START_DATE_MIN, max: `${lastMonth}-${pad2(daysInMonth(lastMonth))}` };
}

/**
 * @param {unknown} dateKey
 * @param {number} [nowMs]
 * @returns {boolean} 실제 날짜이고 범위 안
 */
export function isStartDateAllowed(dateKey, nowMs = Date.now()) {
  if (!isDateKey(dateKey)) return false;
  const { min, max } = startDateRange(nowMs);
  return dateKey >= min && dateKey <= max;
}

// ---------------------------------------------------------------------------
// 범위 보기 — 칩·알약·태그
// ---------------------------------------------------------------------------

/**
 * 금액이 실제로 따르는 범위 키 — 얼린 시작일과 지금 시작일을 이 값끼리 견준다(SETTLEMENT_START_CHANGED 와 같은 판정).
 * 시작일 없음·1일 시작·시작 월 뒤는 모두 'in'(금액이 같다), 시작 월(1일 아님)은 그 날짜, 시작 월 전은 'before_start'.
 * mock 엔진 effectiveStartKey 와 같다(settlementStart.test.mjs 가 둘을 견준다).
 * @param {string} month 'YYYY-MM'
 * @param {string|null} startDate
 * @returns {'in'|'before_start'|string}
 */
export function effectiveStartKey(month, startDate) {
  if (!isDateKey(startDate)) return 'in';
  const startMonth = startDate.slice(0, 7);
  if (month < startMonth) return 'before_start';
  if (month === startMonth && dayOf(startDate) > 1) return startDate;
  return 'in';
}

/**
 * 범위 문자열 → 아는 값. 모르는 값·없음은 'in'
 * @param {unknown} scope
 * @returns {'in'|'start_month'|'before_start'}
 */
export function normalizeScope(scope) {
  return SCOPES.has(scope) ? scope : 'in';
}

/**
 * 시작 전 사용량 짧은 문구(통화 수 · 시간)
 * @param {object|null|undefined} preStart `usage.pre_start` / 목록 `item.pre_start`
 * @returns {string} 예: '12통 · 56분 40초'. 없으면 ''
 */
export function preStartUsageShortText(preStart) {
  const p = obj(preStart);
  if (!p) return '';
  return [
    isNumber(p.real_calls) ? `${formatNumber(p.real_calls)}통` : '',
    isNumber(p.talk_units_10s) ? unitsMinutesText(p.talk_units_10s) : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * 시작 월 근거 줄 — 정산서·엑셀 공용
 * @param {object|null|undefined} preStart
 * @returns {string} 예: '시작일 전 12통 · 56분 40초(청구 제외)'. 없으면 ''
 */
export function preStartUsageText(preStart) {
  const text = preStartUsageShortText(preStart);
  return text ? `시작일 전 ${text}(청구 제외)` : '';
}

/**
 * 시작 월 월 사용료 일할 창
 * @param {object|null|undefined} startMonth `charge.lines.base.start_month`
 * @returns {string} 예: '10/10~10/31 22/31일 일할'. 일수가 없으면 ''
 */
export function startMonthWindowText(startMonth) {
  const sm = obj(startMonth);
  if (!sm || !isNumber(sm.days) || !isNumber(sm.month_days) || sm.days < 1) return '';
  const from = dateOrNull(sm.from) ?? dateOrNull(sm.start_date);
  const to = dateOrNull(sm.to) ?? (from ? addDays(from, sm.days - 1) : null);
  const range = from && to ? `${shortDateText(from)}~${shortDateText(to)} ` : '';
  return `${range}${formatNumber(sm.days)}/${formatNumber(sm.month_days)}일 일할`;
}

/**
 * 범위 보기 — 상세(billing_start), 목록 항목·내보내기 회사(settlement_scope·settlement_start_date)를 같이 받는다.
 * 칸이 없으면 시작일 미설정(지금 화면).
 * @param {object|null|undefined} source
 * @returns {{
 *   kind: 'unset'|'in'|'start_month'|'before_start', scope: 'in'|'start_month'|'before_start',
 *   startDate: string|null, frozenStartDate: string|null, splitActive: boolean, needsRecalc: boolean,
 *   label: string, chip: { text: string, tooltip: string }, tag: { text: string, tooltip: string }|null,
 * }}
 *   needsRecalc: 상세만 — 정산서가 확정이 아니고, 얼린 시작일이 지금과 다르거나 SETTLEMENT_START_CHANGED 경고가 있을 때
 *   (확정 정산서는 다시 계산할 수 없어 띄우지 않는다)
 */
export function settlementScopeView(source) {
  const s = obj(source) ?? {};
  const bs = obj(s.billing_start);
  const startDate = dateOrNull(bs ? bs.start_date : s.settlement_start_date);
  let scope = normalizeScope(bs ? bs.scope : s.settlement_scope);
  // 시작일이 없으면 늘 정산 대상이다 — 'before_start' 만은 서버 판정을 믿는다(정산 대상 아님을 정산 대상으로 보이지 않는다)
  if (startDate === null && scope === 'start_month') scope = 'in';
  const kind = startDate === null && scope === 'in' ? 'unset' : scope;
  const frozenStartDate = bs ? dateOrNull(bs.frozen_start_date) : null;
  const statementStatus = obj(s.statement)?.status ?? null;
  // 서버 경고(SETTLEMENT_START_CHANGED)가 있으면 그것을 믿는다 — 얼린 값이 없는(null) 정산서도 서버가 판정한다
  const changedWarning = arr(s.warnings).some((w) => w?.code === 'SETTLEMENT_START_CHANGED');
  // 얼린 값과 지금 값은 금액이 실제로 따르는 범위(effectiveStartKey)로 견준다 — 서버 경고와 같은 판정(요청서 5절 5·8절).
  // 정산 대상 아님 달은 다시 계산이 409 로 막히고 할 일은 [남은 정산서 지우기]라 띄우지 않는다
  const frozenDiffers =
    frozenStartDate !== null &&
    (isMonthKey(s.month)
      ? effectiveStartKey(s.month, frozenStartDate) !== effectiveStartKey(s.month, startDate)
      : frozenStartDate !== startDate);
  const needsRecalc =
    !!bs && scope !== 'before_start' && statementStatus !== 'confirmed' && (changedWarning || frozenDiffers);
  const splitActive = scope === 'start_month' && startDate !== null && dayOf(startDate) !== 1;
  const short = shortDateText(startDate);
  const preStart = obj(s.usage)?.pre_start ?? s.pre_start;

  let chip;
  let tag = null;
  switch (kind) {
    case 'unset':
      chip = { text: SCOPE_LABELS.unset, tooltip: '정산 시작일을 정하지 않아 이 달 전체를 정산합니다.' };
      break;
    case 'in':
      chip = {
        text: `정산 시작 ${startDate}`,
        tooltip: `정산 시작일(${startDate}) 뒤 달이라 이 달 전체를 정산합니다.`,
      };
      break;
    case 'start_month': {
      const tooltip = splitActive
        ? `${short} 00:00(KST)부터 청구합니다. 그 전 사용량은 청구에서 빼고, 월 사용료·회선은 시작일부터 일할합니다.`
        : `${short}부터 정산합니다. 이 달 전체가 정산 대상입니다.`;
      chip = { text: `정산 시작 ${short} · 시작 월`, tooltip };
      let tagTip = tooltip;
      if (splitActive) {
        const calls = num(obj(preStart)?.real_calls);
        tagTip =
          calls !== null && calls > 0
            ? `${short}부터 청구합니다. 그 전 사용량 ${formatNumber(calls)}통은 청구에서 뺍니다.`
            : `${short}부터 청구합니다. 그 전 사용량은 청구에서 뺍니다.`;
      }
      tag = { text: `시작 ${short}`, tooltip: tagTip };
      break;
    }
    default: {
      const tooltip = notBillableTooltip(startDate);
      chip = { text: startDate ? `정산 시작 ${startDate}` : SCOPE_LABELS.before_start, tooltip };
      tag = { text: SCOPE_LABELS.before_start, tooltip };
    }
  }
  return { kind, scope, startDate, frozenStartDate, splitActive, needsRecalc, label: SCOPE_LABELS[kind], chip, tag };
}

// ---------------------------------------------------------------------------
// 정산 대상 아님 달
// ---------------------------------------------------------------------------

/**
 * 정산 대상 아님 달의 정산 탭 판단 — StatementPanel·SettleTab·OneOffItems 가 쓴다
 * @param {object|null|undefined} detail 상세 응답
 * @returns {{
 *   notBillable: boolean, startDate: string|null, emptyText: string,
 *   leftover: { status: string, rev: number|null, total: number|null }|null, leftoverText: string,
 *   leftoverAction: 'cleanup'|'reopen'|null,
 *   hide: { confirm: boolean, save: boolean, recalculate: boolean, reopen: boolean, oneOffAdd: boolean, oneOffEdit: boolean },
 * }}
 *   일회성 지우기는 늘 남긴다(hide 에 없다). 남은 정산서가 확정이면 재수정은 허락한다(reopen:false).
 */
export function notBillableView(detail) {
  const notBillable = isNotBillableDetail(detail);
  const startDate = settlementScopeView(detail).startDate;
  const st = notBillable ? obj(detail?.statement) : null;
  const leftover = st
    ? { status: typeof st.status === 'string' ? st.status : 'review', rev: num(st.rev), total: num(st.total) }
    : null;
  const confirmed = leftover?.status === 'confirmed';
  let leftoverText = '';
  if (leftover) {
    leftoverText = confirmed
      ? '확정된 정산서가 남아 있어 재수정한 뒤 지울 수 있습니다.'
      : '이 달에 남은 정산서가 있습니다.';
  }
  return {
    notBillable,
    startDate,
    emptyText: notBillable
      ? `정산 시작일${startDate ? `(${startDate})` : ''} 전 달이라 정산하지 않습니다. 사용량과 원가는 참고로 보입니다.`
      : '',
    leftover,
    leftoverText,
    leftoverAction: leftover ? (confirmed ? 'reopen' : 'cleanup') : null,
    hide: {
      confirm: notBillable,
      save: notBillable,
      recalculate: notBillable,
      reopen: notBillable && !confirmed,
      oneOffAdd: notBillable,
      oneOffEdit: notBillable,
    },
  };
}

/**
 * 409 SETTLEMENT_NOT_STARTED(정산 대상 아님 달의 사람 동작) 인가
 * @param {unknown} err
 * @returns {boolean}
 */
export function isNotStartedError(err) {
  return getCallApiErrorCode(err) === 'SETTLEMENT_NOT_STARTED';
}

// ---------------------------------------------------------------------------
// 미리보기 → 모달 표
// ---------------------------------------------------------------------------

/** 정산서 상태(계획서 4-4) */
export const START_STATEMENT_STATUS_TEXT = Object.freeze({
  estimating: '없음',
  closing: '마감 중',
  review: '검토 중',
  confirmed: '확정',
  failed: '마감 실패',
});

/** 할 일 문구 */
export const START_ACTION_TEXT = Object.freeze({
  none: EMPTY,
  recalculate_needed: '다시 계산',
  discard_required: '정산서 지움',
  close_needed: '한 회사 마감 필요',
  auto_close_pending: '자동 마감 대기',
  blocked_confirmed: '재수정 먼저',
  blocked_billed: '지울 수 없음(청구됨)',
  blocked_close_in_progress: '마감 끝난 뒤',
});

const ACTION_TONE = {
  none: 'muted',
  recalculate_needed: 'warn',
  discard_required: 'danger',
  close_needed: 'warn',
  auto_close_pending: 'ok',
  blocked_confirmed: 'danger',
  blocked_billed: 'danger',
  blocked_close_in_progress: 'danger',
};

const statementStatusText = (status) =>
  status === null || status === undefined || status === ''
    ? START_STATEMENT_STATUS_TEXT.estimating
    : START_STATEMENT_STATUS_TEXT[status] ?? String(status);

/** 범위 문구 — 시작 월이면 그 시작일 */
function scopeText(scope, startDate) {
  const s = normalizeScope(scope);
  if (s === 'start_month') return startDate ? `시작 월(${shortDateText(startDate)})` : SCOPE_LABELS.start_month;
  return SCOPE_LABELS[s];
}

const monthsText = (months) => arr(months).filter((m) => typeof m === 'string' && m).join('·');

/**
 * 막힘 문구(해결 방법 포함, 계획서 4-4 그대로)
 * @param {string} code 'SETTLEMENT_START_CONFIRMED_MONTH' 등(앞의 'SETTLEMENT_START_' 는 없어도 된다)
 * @param {string[]} months
 * @returns {string}
 */
export function startBlockerText(code, months) {
  const key = String(code ?? '').replace(/^SETTLEMENT_START_/, '');
  const m = monthsText(months);
  switch (key) {
    case 'CONFIRMED_MONTH':
      return m
        ? `${m} 은 확정된 달입니다. 그 달을 재수정한 뒤 바꾸세요.`
        : '확정된 달이 있습니다. 그 달을 재수정한 뒤 바꾸세요.';
    case 'CLOSE_IN_PROGRESS':
      return m
        ? `${m} 마감이 진행 중입니다. 끝난 뒤 다시 시도하세요.`
        : '마감이 진행 중인 달이 있습니다. 끝난 뒤 다시 시도하세요.';
    case 'BILLED_MONTH':
      return `${
        m ? `${m} 는 이미 청구된 달(재수정했거나 결제 문서가 있음)이라` : '이미 청구된 달(재수정했거나 결제 문서가 있음)이 있어'
      } 정산서를 지울 수 없습니다. 그 달을 0원으로 확정하거나 결제 문서를 무효 처리한 뒤 바꾸세요.`;
    default:
      return m ? `${m} 때문에 정산 시작일을 바꿀 수 없습니다.` : '정산 시작일을 바꿀 수 없는 달이 있습니다.';
  }
}

/** 막힘에 [그 달 열기]를 둘지(확정·청구된 달) */
const BLOCKER_OPENS_MONTH = new Set(['SETTLEMENT_START_CONFIRMED_MONTH', 'SETTLEMENT_START_BILLED_MONTH']);

/**
 * 지울 정산서 한 줄 문구
 * @param {string} month
 * @param {{ status?: string, total?: number, human_touched?: boolean }|null} statement
 * @returns {string} 예: '2026-09 정산서(검토 중, 합계 12,345원, 사람이 고침)를 지웁니다.'
 */
export function discardText(month, statement) {
  const st = obj(statement) ?? {};
  const parts = [statementStatusText(st.status ?? 'review')];
  if (isNumber(st.total)) parts.push(`합계 ${formatWon(st.total)}`);
  if (st.human_touched === true) parts.push('사람이 고침');
  return `${month} 정산서(${parts.join(', ')})를 지웁니다.`;
}

/**
 * 미리보기 응답 → 모달 표·막힘·지울 정산서
 * @param {object|null|undefined} preview GET settlement-start/preview 응답
 * @returns {{
 *   current: { startDate: string|null, rev: number }, next: { startDate: string|null },
 *   rows: Array<{ month: string, beforeText: string, afterText: string, statementText: string, actionText: string,
 *     action: string, tone: 'muted'|'warn'|'danger'|'ok' }>,
 *   blockers: Array<{ code: string, months: string[], text: string, openMonth: string|null }>,
 *   discards: Array<{ month: string, rev: number|null, text: string }>,
 *   canSave: boolean,
 * }}
 */
export function startPreviewRows(preview) {
  const p = obj(preview) ?? {};
  const current = {
    startDate: dateOrNull(obj(p.current)?.start_date),
    rev: Number.isInteger(obj(p.current)?.rev) ? p.current.rev : 0,
  };
  const next = { startDate: dateOrNull(obj(p.next)?.start_date) };
  const affected = arr(p.affected_months).filter((r) => obj(r) && typeof r.month === 'string');
  const rows = affected.map((r) => {
    const action = typeof r.action === 'string' ? r.action : 'none';
    return {
      month: r.month,
      beforeText: scopeText(r.before_scope, current.startDate),
      afterText: scopeText(r.after_scope, next.startDate),
      statementText: statementStatusText(r.statement_status),
      actionText: START_ACTION_TEXT[action] ?? action,
      action,
      tone: ACTION_TONE[action] ?? 'warn',
    };
  });
  const blockers = arr(p.blockers)
    .filter((b) => obj(b) && typeof b.code === 'string')
    .map((b) => {
      const months = arr(b.months).filter((m) => typeof m === 'string');
      return {
        code: b.code,
        months,
        text: startBlockerText(b.code, months),
        openMonth: BLOCKER_OPENS_MONTH.has(b.code) ? months[0] ?? null : null,
      };
    });
  const discards = affected
    .filter((r) => r.action === 'discard_required')
    .map((r) => {
      const st = obj(r.statement);
      return { month: r.month, rev: num(st?.rev), text: discardText(r.month, st ?? { status: r.statement_status }) };
    });
  return { current, next, rows, blockers, discards, canSave: blockers.length === 0 };
}

// ---------------------------------------------------------------------------
// 오류 → 문구와 다음 동작
// ---------------------------------------------------------------------------

/**
 * PUT·preview·GET 오류 → 문구와 다음 동작(모달이 고른다)
 * @param {unknown} err
 * @returns {{ code: string|null, text: string, next: 'reload'|'repreview'|'blocked'|'refresh'|'keep'|'unimplemented', months: string[] }}
 *   reload: GET 다시 + 미리보기 다시 · repreview: 미리보기 다시 · blocked: 막힘 표에 ·
 *   refresh: 모달 안 오류 줄 + GET 만 다시(rev — 서버가 보상 CAS 로 시작일을 되돌리며 rev 를 올렸을 수 있다. 요청서 3-3 처리
 *   순서 3). 입력·미리보기·확인 체크는 그대로 · keep: 모달 안 오류 줄 · unimplemented: 배포 전 안내 + 저장 막음
 */
export function startErrorText(err) {
  const body = getCallApiErrorBody(err);
  const code = body?.error?.code ?? null;
  const details = obj(body?.error?.details) ?? {};
  const months = arr(details.months).length
    ? arr(details.months).filter((m) => typeof m === 'string')
    : arr(details.statements)
        .map((s) => s?.month)
        .filter((m) => typeof m === 'string');
  const make = (text, next) => ({ code, text, next, months });
  switch (code) {
    case 'SETTLEMENT_START_REV_CHANGED':
      return make('다른 사람이 먼저 바꿨습니다. 새로 불러옵니다.', 'reload');
    case 'SETTLEMENT_START_DISCARD_REQUIRED':
      return make('지울 정산서가 그사이 바뀌었습니다. 바뀐 목록을 다시 확인하세요.', 'repreview');
    case 'SETTLEMENT_START_CONFIRMED_MONTH':
    case 'SETTLEMENT_START_CLOSE_IN_PROGRESS':
    case 'SETTLEMENT_START_BILLED_MONTH':
      return make(startBlockerText(code, months), 'blocked');
    case 'AUDIT_UNAVAILABLE':
      return make('이용 내역을 남길 수 없어 시작일을 바꾸지 않았습니다. 잠시 후 다시 시도하세요.', 'refresh');
    case 'PERM_UNAVAILABLE':
      return make('권한을 확인하지 못해 시작일을 바꾸지 않았습니다. 잠시 뒤 다시 시도하세요.', 'refresh');
    case 'PERMISSION_DENIED':
      return make('정산 시작일을 바꿀 권한이 없습니다. 관리자에게 권한을 요청하세요.', 'keep');
    case 'SUPPORT_SESSION_ENDED':
      return make(`${supportEndedText(body?.error?.reason)} 시작일을 바꾸지 않았습니다.`, 'keep');
    case 'VALIDATION_ERROR': {
      const msg = body?.error?.message;
      return make(hangul(msg) ? msg : '입력값을 확인하세요.', 'keep');
    }
    default:
      break;
  }
  if (isCallApiUnimplemented(err)) return make(START_UNIMPLEMENTED_TEXT, 'unimplemented');
  if (getCallApiErrorStatus(err) === 400) {
    const msg = body?.error?.message;
    return make(hangul(msg) ? msg : '입력값을 확인하세요.', 'keep');
  }
  return make(START_SAVE_FAILED_TEXT, 'keep');
}

/**
 * 미리보기(GET preview) 오류 → 문구. 저장 문구('…바꾸지 않았습니다'·'저장하지 못했습니다')를 쓰지 않는다 — 아직 저장 전이다.
 * @param {unknown} err
 * @returns {{ code: string|null, text: string, retry: boolean, unimplemented: boolean }}
 *   retry: 같은 입력으로 다시 받으면 풀릴 수 있다([다시 불러오기]) · unimplemented: 배포 전 안내 + 저장 막음
 */
export function startPreviewErrorText(err) {
  const body = getCallApiErrorBody(err);
  const code = body?.error?.code ?? null;
  const make = (text, retry, unimplemented = false) => ({ code, text, retry, unimplemented });
  if (code === 'PERMISSION_DENIED') return make('정산 시작일을 바꿀 권한이 없습니다. 관리자에게 권한을 요청하세요.', false);
  if (code === 'SUPPORT_SESSION_ENDED') return make(supportEndedText(body?.error?.reason), false);
  if (code === 'VALIDATION_ERROR' || getCallApiErrorStatus(err) === 400) {
    const msg = body?.error?.message;
    return make(hangul(msg) ? msg : '입력값을 확인하세요.', false);
  }
  if (isCallApiUnimplemented(err)) return make(START_UNIMPLEMENTED_TEXT, false, true);
  return make(START_PREVIEW_FAILED_TEXT, true);
}

// ---------------------------------------------------------------------------
// 저장 결과 안내
// ---------------------------------------------------------------------------

const RESULT_NOTICE = {
  close_needed: (m) => ({
    text: `${m} 이 정산 대상이 됐습니다. 이 달은 자동 마감이 다시 돌지 않으니 한 회사 마감을 실행하세요.`,
    action: 'close_company',
  }),
  auto_close_pending: (m) => ({
    text: `${m} 이 정산 대상이 됐습니다. 자동 마감이 다시 돌 때 정산서가 만들어집니다.`,
    action: null,
  }),
  discard_conflict: (m) => ({
    text: `${m} 정산서가 그사이 바뀌어 지우지 않았습니다. 바뀐 내용을 확인한 뒤 [남은 정산서 지우기]를 누르세요.`,
    action: 'cleanup',
  }),
  confirmed_during_change: (m) => ({
    text: `${m} 에 그사이 확정된 정산서가 남았습니다. 그 달을 재수정한 뒤 다시 지우세요.`,
    action: 'reopen',
  }),
  recalculate_needed: (m) => ({ text: `${m} 은 시작일이 바뀌어 다시 계산해야 확정할 수 있습니다.`, action: null }),
  discarded: (m) => ({ text: `${m} 정산서를 지웠습니다.`, action: null }),
};

/**
 * 저장 결과 안내 — PUT 응답 affected_months 순서대로. none·모르는 action 은 넣지 않는다
 * @param {object|null|undefined} putResponse
 * @returns {Array<{ month: string, kind: string, text: string, action: 'close_company'|'cleanup'|'reopen'|null }>}
 */
export function startResultNotices(putResponse) {
  return arr(obj(putResponse)?.affected_months)
    .filter((r) => obj(r) && typeof r.month === 'string' && Object.prototype.hasOwnProperty.call(RESULT_NOTICE, r.action))
    .map((r) => ({ month: r.month, kind: r.action, ...RESULT_NOTICE[r.action](r.month) }));
}

// ---------------------------------------------------------------------------
// 모달 검증 · 본문
// ---------------------------------------------------------------------------

const REASON_VALUES = new Set(START_REASONS.map((r) => r.value));

/**
 * 모달 검증 — 첫 문제 문구 하나(없으면 ''). 화면은 message.warning 으로 보인다
 * @param {{ clear?: boolean, startDate?: string|null, reasonCode?: string|null, memo?: string|null,
 *   discards?: Array<object>|null, acknowledged?: boolean, nowMs?: number }} input
 * @returns {string}
 */
export function startFormError({ clear = false, startDate = null, reasonCode = null, memo = '', discards = [], acknowledged = false, nowMs = Date.now() } = {}) {
  if (!clear) {
    if (!startDate) return '새 시작일을 고르세요.';
    if (!isStartDateAllowed(startDate, nowMs)) {
      const { min, max } = startDateRange(nowMs);
      return `${min} ~ ${max} 사이 날짜를 고르세요.`;
    }
  }
  if (!REASON_VALUES.has(reasonCode)) return '바꾸는 이유를 고르세요.';
  const text = typeof memo === 'string' ? memo.trim() : '';
  if (!text) return '메모를 적어 주세요.';
  if (text.length > START_MEMO_MAX) return `메모는 ${START_MEMO_MAX}자까지 적을 수 있습니다.`;
  if (arr(discards).length && acknowledged !== true) return '지울 정산서를 확인했다고 체크하세요.';
  return '';
}

/**
 * PUT 본문 — 다섯 키만(서버 전용 Pipe 는 모르는 키 400). discards 가 비면 acknowledge_discard 키를 넣지 않는다
 * @param {{ clear?: boolean, startDate?: string|null, rev?: number, reasonCode: string, memo: string,
 *   discards?: Array<{ month: string, rev: number|null }> }} input
 * @returns {{ start_date: string|null, expected_rev: number, reason_code: string, memo: string,
 *   acknowledge_discard?: Array<{ month: string, rev: number|null }> }}
 */
export function buildSettlementStartBody({ clear = false, startDate = null, rev = 0, reasonCode, memo, discards = [] } = {}) {
  const body = {
    start_date: clear ? null : startDate ?? null,
    expected_rev: Number.isInteger(rev) ? rev : 0,
    reason_code: reasonCode,
    memo: typeof memo === 'string' ? memo.trim() : '',
  };
  const ack = arr(discards).map((d) => ({ month: d.month, rev: d.rev }));
  if (ack.length) body.acknowledge_discard = ack;
  return body;
}

// ---------------------------------------------------------------------------
// 지금 값 · 변경 이력
// ---------------------------------------------------------------------------

/** ISO 문자열 또는 ms → ms(아니면 null) */
function msOf(v) {
  if (isNumber(v)) return v;
  if (typeof v === 'string' && v) {
    const t = Date.parse(v);
    return Number.isFinite(t) ? t : null;
  }
  return null;
}

/** 이유 코드 → 이름(모르는 값은 그대로) */
export function startReasonText(code) {
  if (code === null || code === undefined || code === '') return EMPTY;
  return START_REASONS.find((r) => r.value === code)?.label ?? String(code);
}

/**
 * 모달 머리 '지금: …'
 * @param {object|null|undefined} startDoc GET settlement-start 응답
 * @returns {string} 예: '지금: 2026-10-01 (운영자 이름, 10-28)' | '지금: 미설정'
 */
export function startCurrentText(startDoc) {
  const doc = obj(startDoc) ?? {};
  const date = dateOrNull(doc.start_date);
  if (!date) return '지금: 미설정';
  const at = msOf(doc.updated_at);
  const parts = [];
  if (typeof doc.updated_by_name === 'string' && doc.updated_by_name.trim()) parts.push(doc.updated_by_name.trim());
  if (at !== null) {
    const p = kstParts(at);
    parts.push(`${pad2(p.month)}-${pad2(p.day)}`);
  }
  return parts.length ? `지금: ${date} (${parts.join(', ')})` : `지금: ${date}`;
}

/**
 * 변경 이력 표(최신 먼저, 최대 20 — 서버 순서 그대로)
 * @param {object|null|undefined} startDoc
 * @returns {Array<{ rev: number|null, atText: string, actor: string, beforeText: string, afterText: string,
 *   reasonText: string, memo: string, discardedText: string }>}
 */
export function startHistoryRows(startDoc) {
  return arr(obj(startDoc)?.history)
    .filter((h) => obj(h))
    .slice(0, 20)
    .map((h) => {
      const discarded = arr(h.discarded_months).filter((m) => typeof m === 'string');
      return {
        rev: num(h.rev),
        atText: formatKstDateTime(msOf(h.at)),
        actor: typeof h.actor_name === 'string' && h.actor_name ? h.actor_name : EMPTY,
        beforeText: dateOrNull(h.before) ?? '미설정',
        afterText: dateOrNull(h.after) ?? '미설정',
        reasonText: startReasonText(h.reason_code),
        memo: typeof h.memo === 'string' ? h.memo : '',
        discardedText: discarded.length ? `${discarded.join('·')} 정산서 지움` : '',
      };
    });
}
